import {
  Briefcase,
  Wallet,
  HeartPulse,
  Rocket,
  BookOpen,
  Plane,
  Brain,
  Heart,
  Baby,
  Smile,
  GraduationCap,
  NotebookPen,
  BriefcaseBusiness,
  ClipboardList,
  Mic,
  PiggyBank,
  PencilRuler,
  Coins,
  ListChecks,
  Sprout,
  Flame,
  Blocks,
  Network,
} from "lucide-react";

export const categories = [
  {
    key: "career",
    label: "Career",
    icon: Briefcase,
    copy: "Get promoted, change careers, land your dream job, negotiate your salary, or build new skills.",
    goal: "Land the role you deserve.",
    points: ["Resume & interview prep", "Salary negotiation", "Networking playbooks", "Skill roadmaps"],
    video: "/media/career.mp4",
  },
  {
    key: "financial",
    label: "Financial",
    icon: Wallet,
    copy: "Pay off debt, build wealth, save for retirement, invest wisely, and achieve financial freedom.",
    goal: "Build lasting wealth.",
    points: ["Debt payoff plans", "Smart budgeting", "Investing guidance", "Retirement tracking"],
    video: "/media/financial.mp4",
  },
  {
    key: "health",
    label: "Health & Fitness",
    icon: HeartPulse,
    copy: "Lose weight, build muscle, improve nutrition, sleep better, and create habits that last.",
    goal: "Feel strong every day.",
    points: ["Custom workouts", "Nutrition & macros", "Sleep optimization", "Habit building"],
    video: "/media/health.mp4",
  },
  {
    key: "business",
    label: "Business",
    icon: Rocket,
    copy: "Launch a business, grow revenue, find customers, increase productivity, and scale faster.",
    goal: "Launch and scale faster.",
    points: ["Go-to-market plans", "Marketing & sales", "Hiring & ops", "Fundraising prep"],
    video: "/media/business.mp4",
  },
  {
    key: "learn",
    label: "Learn",
    icon: BookOpen,
    copy: "Master a language, learn AI, earn certifications, read more books, or develop any new skill.",
    goal: "Master any skill.",
    points: ["Study plans", "Daily practice", "Certifications", "Reading goals"],
    video: "/media/learn.mp4",
  },
  {
    key: "travel",
    label: "Travel",
    icon: Plane,
    copy: "Plan dream vacations, save for travel, build bucket lists, and explore the world.",
    goal: "See the world.",
    points: ["Trip planning", "Travel savings", "Bucket lists", "Booking reminders"],
    video: "/media/travel.mp4",
  },
  {
    key: "growth",
    label: "Personal Growth",
    icon: Brain,
    copy: "Develop confidence, improve productivity, build better habits, and become who you want to be.",
    goal: "Become your best self.",
    points: ["Confidence building", "Better habits", "Focus & productivity", "Daily reflection"],
    video: "/media/growth.mp4",
  },
  {
    key: "relationship",
    label: "Relationship",
    icon: Heart,
    copy: "Strengthen your marriage, deepen friendships, communicate with intention, and never miss what matters.",
    goal: "Love that lasts.",
    points: ["Communication tools", "Date ideas", "Anniversary reminders", "Conflict resolution"],
    video: "/media/relationship.mp4",
  },
  {
    key: "parenting",
    label: "Parenting",
    icon: Baby,
    copy: "Build routines, navigate milestones, balance screen time, and show up as the parent you want to be.",
    goal: "Parent with intention.",
    points: ["Age-based routines", "Milestone tracking", "Screen-time balance", "Quality-time ideas"],
    video: "/media/parenting.mp4",
  },
  {
    key: "mental",
    label: "Mental",
    icon: Smile,
    copy: "Reflect, manage stress, build resilience, and protect your focus and mindset day to day.",
    goal: "A calmer, clearer mind.",
    points: ["Guided reflection", "Stress management", "Resilience building", "Focus routines"],
    video: "/media/mental.mp4",
  },
];

export const steps = [
  {
    n: "01",
    title: "Dream",
    copy: "Tell b→come what you want out of life. Lose 50 pounds, retire early, start a business, or travel the world—every journey starts with your vision.",
  },
  {
    n: "02",
    title: "Plan",
    copy: "Your AI builds a personalized roadmap—breaking your biggest dreams into achievable milestones, weekly priorities, and daily actions around your schedule.",
  },
  {
    n: "03",
    title: "Take Action",
    copy: "Every day, your AI coach tells you exactly what to focus on. No guessing. No overwhelm. Just clear priorities that move you closer to your goals.",
  },
  {
    n: "04",
    title: "Become",
    copy: "Track your progress, celebrate wins, overcome setbacks, and watch your life transform one day at a time.",
  },
];

export const coaches = [
  {
    title: "Career Coach",
    copy: "Navigate promotions, interviews, resumes, networking, and salary growth.",
  },
  {
    title: "Financial Coach",
    copy: "Budget smarter, eliminate debt, invest confidently, and build long-term wealth.",
  },
  {
    title: "Fitness Coach",
    copy: "Personalized workouts, nutrition guidance, recovery, and habit building.",
  },
  {
    title: "Business Coach",
    copy: "Launch, market, sell, automate, and grow your business.",
  },
  {
    title: "Learning Coach",
    copy: "Create study plans, stay consistent, and master new skills faster.",
  },
  {
    title: "Productivity Coach",
    copy: "Prioritize your day, manage your calendar, and maximize your time.",
  },
  {
    title: "Relationship Coach",
    copy: "Strengthen your marriage, deepen friendships, and communicate with intention.",
  },
  {
    title: "Parenting Coach",
    copy: "Build routines, navigate milestones, and show up as the parent you want to be.",
  },
  {
    title: "Mental Coach",
    copy: "Reflect, manage stress, build resilience, and protect your focus and mindset.",
  },
];

export const scores = [
  { label: "Health", value: 82 },
  { label: "Wealth", value: 76 },
  { label: "Career", value: 91 },
  { label: "Business", value: 94 },
  { label: "Learning", value: 80 },
  { label: "Relationships", value: 88 },
];

export const editions = {
  student: {
    key: "student",
    tab: "Student Edition",
    icon: GraduationCap,
    headline: "Your AI Study Partner for School, College & Beyond.",
    intro:
      "Success isn't just about studying harder — it's about having the right plan. b→come Student helps students stay organized, build better habits, prepare for their careers, and confidently achieve their academic and professional goals. Whether you're preparing for your next exam, applying for internships, or planning your future career, your AI coach is with you every step of the way.",
    cta: "Start Building Your Future Today",
    featuresTitle: "Everything You Need to Succeed",
    features: [
      {
        icon: NotebookPen,
        title: "AI Study Planning",
        tagline: "Never wonder what to study next again.",
        copy: "Create personalized study schedules, organize assignments, prepare for exams, and stay ahead of deadlines with an AI coach that adapts to your workload.",
      },
      {
        icon: BriefcaseBusiness,
        title: "Career Preparation",
        tagline: "Start preparing for your dream career before graduation.",
        copy: "Get help building your resume, improving LinkedIn, exploring career paths, developing new skills, and preparing for interviews.",
      },
      {
        icon: ClipboardList,
        title: "Internship Tracker",
        tagline: "Never miss another opportunity.",
        copy: "Track internship applications, deadlines, interview dates, follow-ups, and offers — all in one place.",
      },
      {
        icon: Mic,
        title: "Interview Practice",
        tagline: "Practice with an AI interviewer anytime.",
        copy: "Receive realistic interview questions, instant feedback, confidence coaching, and personalized recommendations to improve every interview.",
      },
      {
        icon: PiggyBank,
        title: "Financial Literacy",
        tagline: "Build smart money habits early.",
        copy: "Learn budgeting, saving, investing, managing student loans, building credit, and creating long-term financial success.",
      },
      {
        icon: Network,
        title: "Networking Strategies",
        tagline: "Build the connections that open doors.",
        copy: "Learn how to reach out to mentors, grow your professional network, make a strong impression, and turn conversations into opportunities.",
      },
    ],
    closingTitle: "Graduate With More Than A Degree",
    closingCopy:
      "The students who thrive after graduation aren't always the smartest — they're the most prepared. b→come helps you develop the habits, confidence, and skills employers are looking for, before you ever enter the workforce.",
  },
  kids: {
    key: "kids",
    tab: "Kids Edition",
    icon: Blocks,
    headline: "Helping Kids Build Great Habits That Last a Lifetime.",
    intro:
      "Learning doesn't stop when school ends. With b→come Kids, children get a fun, encouraging AI coach that helps them stay organized, complete responsibilities, build confidence, and develop positive habits they'll carry into adulthood. Parents stay informed while kids stay motivated.",
    cta: "Give Your Child a Head Start",
    featuresTitle: "Built for Growing Minds",
    features: [
      {
        icon: PencilRuler,
        title: "Homework Planner",
        tagline: "No more forgotten assignments.",
        copy: "Organize homework, projects, tests, and school schedules with friendly reminders and age-appropriate guidance.",
      },
      {
        icon: BookOpen,
        title: "Reading Goals",
        tagline: "Make reading fun and rewarding.",
        copy: "Set reading goals, track completed books, earn achievements, and develop a lifelong love of learning.",
      },
      {
        icon: Coins,
        title: "Allowance Tracker",
        tagline: "Teach financial responsibility early.",
        copy: "Track allowance, savings goals, spending, and earning opportunities while introducing healthy money habits in a simple, engaging way.",
      },
      {
        icon: ListChecks,
        title: "Chores & Responsibilities",
        tagline: "Turn daily responsibilities into achievements.",
        copy: "Create chore lists, encourage independence, celebrate completed tasks, and build accountability through positive reinforcement.",
      },
      {
        icon: Sprout,
        title: "Habit Builder",
        tagline: "Healthy habits start young.",
        copy: "Help children develop routines around sleep, exercise, kindness, organization, gratitude, and responsibility with personalized encouragement.",
      },
      {
        icon: Flame,
        title: "Learning Streaks",
        tagline: "Celebrate consistency.",
        copy: "Reward daily progress with streaks, milestones, badges, and achievements that make learning and personal growth exciting.",
      },
    ],
    closingTitle: "Raising Confident, Independent Kids",
    closingCopy:
      "Every great future starts with small daily habits. b→come Kids helps children become more organized, responsible, confident, and prepared for whatever comes next — all while giving parents peace of mind that their child is building skills for life.",
  },
};

export const LOGO_URL = "/media/logo.png";
export const LOGO_DARK_URL = "/media/logo-dark.png";

export const IMAGES = {
  heroAbstract:
    "https://images.unsplash.com/photo-1773053525984-3857c1e0be44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdsb3dpbmclMjBnb2xkJTIwZW5lcmd5fGVufDB8fHx8MTc4Mzk5MDUxMnww&ixlib=rb-4.1.0&q=85",
  statementPortrait:
    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBwb3J0cmFpdCUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzgzOTkwNTEyfDA&ixlib=rb-4.1.0&q=85",
};
